import { createFileRoute } from "@tanstack/react-router";
import { Edit05 } from "@untitledui/icons";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { Input } from "@/components/base/input/input";
import { Label } from "@/components/base/label";
import { TextArea } from "@/components/base/textarea/textarea";
import { EmptyValue } from "@/components/common/EmptyValue";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import { ActivateLifecycleButton } from "@/components/Organisms/ActivateLifecycleModal/ActivateLifecycleButton";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { getEntityTypeClassLabel } from "@/services/lifecycle/lifecycle.types";
import {
	formatLifecycleVersion,
	getLifecycleDisplayName,
	getLifecycleStateLabel,
} from "@/utils/lifecycle-detail/lifecycle-detail.utils";
import { useLifecycleDrawerContext } from "@/utils/lifecycle-detail/lifecycle-drawer-context";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
)({
	component: LifecycleDetailsTabContent,
});

function LifecycleDetailsTabContent() {
	const { t } = useAppTranslation();
	const {
		lifecycle,
		mode,
		setMode,
		canEditDetails,
		canEditStructure,
		isDraft,
		organizationId,
		draftName,
		draftDescription,
		setDraftName,
		setDraftDescription,
	} = useLifecycleDrawerContext();

	const sourceLabel =
		lifecycle.isDefault === true
			? t("admin.lifecycles.table.source.system")
			: t("admin.lifecycles.table.source.custom");

	const isEditMode = mode === "edit" && canEditDetails;

	const stateLabel =
		getLifecycleStateLabel(lifecycle) === "active"
			? t("admin.lifecycles.drawer.details.state.active")
			: t("admin.lifecycles.drawer.details.state.draft");
	const categoryTypeLabel = getEntityTypeClassLabel(lifecycle.entityTypeClass);

	return (
		<div className="space-y-4 px-5 pb-5">
			<div className="flex items-start justify-between gap-3">
				<div>
					<h3 className="text-[16px] font-semibold text-primary">
						{t("admin.lifecycles.drawer.details.title")}
					</h3>
					<p className="text-sm text-gray-500">
						{t("admin.lifecycles.drawer.details.description")}
					</p>
				</div>
				<div className="flex items-center gap-2">
					{isDraft && canEditStructure && (
						<ActivateLifecycleButton
							data-testid="lifecycle-details-activate-btn"
							size="sm"
							lifecycleId={lifecycle.id}
							lifecycleName={getLifecycleDisplayName(lifecycle) ?? lifecycle.id}
							hasInitialStatus={Boolean(lifecycle.initialStatus?.id)}
							organizationId={organizationId}
						/>
					)}
					{!isEditMode && canEditDetails && (
						<Button
							data-testid="lifecycle-details-edit-btn"
							aria-label={t("admin.lifecycles.drawer.details.editTooltip")}
							color="secondary"
							size="sm"
							className="p-2 border border-gray-300 border-solid"
							onClick={() => setMode("edit")}
						>
							<Edit05 size={20} style={{ stroke: "var(--color-gray-400)" }} />
						</Button>
					)}
				</div>
			</div>

			<div className="rounded-lg bg-gray-50 p-4">
				<DetailFieldSection>
					<DetailFieldGrid>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.name",
								).toUpperCase()}
								value={getLifecycleDisplayName(lifecycle) || <EmptyValue />}
								valueClassName="font-bold text-gray-900"
								isEditMode={isEditMode}
								editComponent={
									<Input
										data-testid="lifecycle-details-name-input"
										label={t(
											"admin.lifecycles.drawer.details.fields.name",
										).toUpperCase()}
										value={draftName}
										onChange={setDraftName}
										placeholder={t(
											"admin.lifecycles.drawer.details.fields.namePlaceholder",
										)}
										aria-label={t(
											"admin.lifecycles.drawer.details.fields.name",
										)}
									/>
								}
							/>
						</div>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.version",
								).toUpperCase()}
								value={formatLifecycleVersion(lifecycle)}
								valueClassName="font-bold text-gray-900"
							/>
						</div>
					</DetailFieldGrid>
					<DetailFieldGrid>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.description",
								).toUpperCase()}
								value={lifecycle.description || <EmptyValue />}
								valueClassName="font-bold text-gray-900"
								isEditMode={isEditMode}
								editComponent={
									<TextArea
										data-testid="lifecycle-details-description-input"
										label={t(
											"admin.lifecycles.drawer.details.fields.description",
										).toUpperCase()}
										value={draftDescription}
										onChange={setDraftDescription}
										placeholder={t(
											"admin.lifecycles.drawer.details.fields.descriptionPlaceholder",
										)}
										rows={3}
									/>
								}
							/>
						</div>
					</DetailFieldGrid>
					<DetailFieldGrid>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.source",
								).toUpperCase()}
								value={
									<Label size="sm" type="dark" showIcon={false}>
										{sourceLabel}
									</Label>
								}
							/>
						</div>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.state",
								).toUpperCase()}
								value={
									<BadgeWithDot
										type="modern"
										size="sm"
										color={
											getLifecycleStateLabel(lifecycle) === "active"
												? "success"
												: "gray"
										}
									>
										{stateLabel}
									</BadgeWithDot>
								}
							/>
						</div>
					</DetailFieldGrid>
					<DetailFieldGrid>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.categoryType",
								).toUpperCase()}
								value={categoryTypeLabel ?? <EmptyValue />}
								valueClassName="font-bold text-gray-900"
							/>
						</div>
						<div className="flex-1">
							<DetailField
								label={t(
									"admin.lifecycles.drawer.details.fields.id",
								).toUpperCase()}
								value={<CopyableId value={lifecycle.id} detailText />}
								valueClassName="font-bold text-gray-900"
							/>
						</div>
					</DetailFieldGrid>
				</DetailFieldSection>
			</div>
		</div>
	);
}
